<template>
  <section
    v-if="compatibleProducts.length > 0"
    class="bg-surface-default-mid rounded-lg p-16"
  >
    <h2
      ref="compatibleWithTitle"
      class="body-1-bold leading-label-large mb-12 [scroll-margin-top:64px] md:[scroll-margin-top:94px]"
    >
      {{ i18n(translations.title) }}
    </h2>

    <LazyHydrationCompatibleWithProductsListDesktop
      class="hidden md:block"
      :compatible-products
      :hydrate-on-media-query="`(min-width: ${DESKTOP_BREAKPOINT}px)`"
      :is-expanded
    />

    <LazyHydrationCompatibleWithProductsListMobile
      class="md:hidden"
      :hidden-products
      :hydrate-on-media-query="`(max-width: ${DESKTOP_BREAKPOINT - 1}px)`"
      :is-collapsible
      :is-expanded
      :visible-products
    />

    <CompatibleWithControls
      :hidden-products
      :is-collapsible
      :is-expanded
      :see-other-url="i18n(translations.seeOtherUrl)"
      @toggle-expanded="toggleExpanded"
    />
  </section>
</template>

<script lang="ts" setup>
import { defineLazyHydrationComponent } from '#imports'
import { computed, shallowRef, useTemplateRef, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { sleep } from '@backmarket/utils/async/sleep'
import { requestAnimationFrame } from '@ds/utils/requestAnimationFrame'

import { DESKTOP_BREAKPOINT } from './CompatibleWith.constants'
import translations from './CompatibleWith.translations'
import CompatibleWithControls from './components/CompatibleWithControls.vue'

const LazyHydrationCompatibleWithProductsListDesktop =
  defineLazyHydrationComponent(
    'mediaQuery',
    () =>
      import('./components/CompatibleWithProductsList/CompatibleWithProductsListDesktop.vue'),
  )
const LazyHydrationCompatibleWithProductsListMobile =
  defineLazyHydrationComponent(
    'mediaQuery',
    () =>
      import('./components/CompatibleWithProductsList/CompatibleWithProductsListMobile.vue'),
  )

const VISIBLE_COUNT = 3

const props = defineProps<{
  compatibleProducts: readonly string[]
}>()

const i18n = useI18n()

const isExpanded = shallowRef(false)

const visibleProducts = computed(() =>
  props.compatibleProducts.slice(0, VISIBLE_COUNT),
)

const hiddenProducts = computed(() =>
  props.compatibleProducts.slice(VISIBLE_COUNT),
)

const isCollapsible = computed(
  () => props.compatibleProducts.length > VISIBLE_COUNT,
)

function toggleExpanded() {
  isExpanded.value = !isExpanded.value
}

const compatibleWithTitle = useTemplateRef('compatibleWithTitle')
watch(isExpanded, async (expanded) => {
  if (!expanded) {
    return
  }

  // we need to wait for the transition in the list component to complete
  await sleep(300)

  requestAnimationFrame(() => {
    const isMobile = window.matchMedia('(max-width: 767px)').matches
    if (!isMobile) {
      return
    }
    compatibleWithTitle.value?.scrollIntoView({
      behavior: 'smooth',
    })
  })
})
</script>
